
import { Component, Vue, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { DispatchGuard, Guard } from "@/store/dispatch/types";
import { EventDetails } from "@/store/site-monitor/types";
import { GPSService } from "@/services/gps.service";
import { FeaturesList } from "@/store/types";
import ManualGuardRow from "./dispatch/ManualGuardRow.vue";
import { AlarmQueueFilterID } from "@/store/eventqueue/types";

const DispatchStore = namespace("dispatch");
const SiteMonitor = namespace("siteMonitor");
const EventQueueStore = namespace("eventqueue");

@Component({
	components: {
		"manual-guard-row": ManualGuardRow,
	}
})
export default class Dispatch extends Vue {
	// Global store
	@Getter isFullUser: boolean;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getMapKey") private mapKey: string;

	// Site monitor store
	@SiteMonitor.Getter("getEventDetails") eventDetails: EventDetails;
	@SiteMonitor.Mutation setActivity: () => void;

	// Dispatch store
	@DispatchStore.Getter("getGuardsForEvent") guardsForEvent: Guard[];
	@DispatchStore.Getter("getDispatchedGuards") dispatchedGuards: DispatchGuard[];

	@DispatchStore.Action dispatchGuard: (dispatchGuardRequest: any) => Promise<boolean>;
	@DispatchStore.Action loadGuardsForEvent: ({ eventId: number, location: string, includeGuardsWithMatchingFilter: boolean })
	 => Promise<void>;
	@DispatchStore.Action clearGuardsForEvent: () => Promise<void>;
	@DispatchStore.Action updateDispatchStatus: (eventId: number) => Promise<void>;
	@DispatchStore.Action updateEventLocation: (location: string) => Promise<void>;

	@EventQueueStore.Getter("getActiveFilterID") currentFilterID!: AlarmQueueFilterID;

	@Prop(Number) public eventId: number;

	public $refs!: {
		searchInput: HTMLInputElement;
		dispatchButton: HTMLButtonElement;
	};

	public searchText: string = "";
	public noLocation: boolean = false;
	public location: string = "";
	public lastCheckedLatLongValue: string = "";

	// Returns the list of available guards for this event, filtered using any text in the search box
	public get filteredGuards() {
		let eventGuards = this.guardsForEvent
			.map(guard => {
				return {
					guardID: guard.guardID,
					callsign: guard.callsign,
					status: guard.status
				};
			})
			.sort();
		let dispatchedGuards = this.dispatchedGuards
			.map(guard => {
				return {
					guardID: guard.guardID,
					callsign: guard.callsign,
					status: guard.status
				};
			})
			.sort();

		let allGuards = [...dispatchedGuards, ...eventGuards];

		// The above arrays can contain duplicates, this removes them
		let filteredGuards = allGuards.reduce(function (accumulator, currentValue) {
			if (!accumulator.some(x => x.guardID == currentValue.guardID)) {
				accumulator.push(currentValue)
			}
			return accumulator
		}, []);

		if (this.searchText == null || this.searchText.trim() == "") {
			return filteredGuards;
		} else {
			return filteredGuards.filter(x => x.callsign.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1);
		}
	}

	public get dispatched() {
		return (this.dispatchedGuards || []).length > 0;
	}

	public mounted() {
		this.updateDispatchStatus(this.eventId);
	}

	public async clearSearch($event) {
		$event.stopPropagation();

		this.searchText = "";
		await this.$nextTick();
		this.$refs.searchInput.focus();
	}

	public async loadDispatch() {
		this.searchText = "";

		await this.clearGuardsForEvent();
		
		if (this.eventDetails.latLong && this.eventDetails.latLong != "") {
			this.noLocation = false;
			let latLong = this.eventDetails.latLong;
			const IsLatLongInCoordinateFormat = /^-?[\d.]+[NS]? -?[\d.]+[WE]?($|,)/.test(latLong);

			if (!IsLatLongInCoordinateFormat) {
				// Have we already checked the location of this event?
				if (this.lastCheckedLatLongValue == latLong) {
					// Yes, but no location was set - so there is no location assocaited with this latLong value
					if (this.location === "") {
						this.noLocation = true;
						return;
					}
					// Yes, location was set - so return the location associated with this latLong value
					else {
						latLong = this.location;
					}
				} else {
					// No, location has not been checked for this event...
					// Update the lastCheckedLatLongValue to this new latLong value
					this.lastCheckedLatLongValue = latLong;

					// Look up the latLong value
					let geoCoder = new GPSService();
					let response = await geoCoder.GeoCodeLookup(latLong, this.mapKey);

					// Any location results associated with provided latLong value?
					if (!response.results || response.results.length == 0) {
						// No results, so this latLong value is not a valid location, set noLocation to true
						// and return - skipping loading guards for this event
						this.noLocation = true;
						return;
					} else {
						// Got results, set latLong to the coordinates associated with this location and continue
						let addressGeometryLocation = response.results[0].geometry.location;
						latLong = this.location = `${addressGeometryLocation.lat} ${addressGeometryLocation.lng}`;
					}
				}
			}

			await this.updateEventLocation(latLong);

			await this.loadGuardsForEvent({
				eventId: this.eventId,
				location: latLong,
				includeGuardsWithMatchingFilter: this.isIncludeGuardsByAlarmQueueFilterEnabled
			});

			if (this.guardsForEvent && this.guardsForEvent.length > 0) {
				Vue.nextTick(() => {
					// Focus the search input when they open the dispatch menu
					this.$refs.searchInput.focus();
				});
			}

			this.updateDispatchStatus(this.eventId);
		} else {
			this.noLocation = true;
		}
	}

	private get isIncludeGuardsByAlarmQueueFilterEnabled(): boolean {
		return this.getFeature(["Alarms", "Dispatch", "IncludeGuardsByAlarmQueueFilter"]);
	}
}
